import React, { useCallback } from 'react';
import { Info, Loader2 } from 'lucide-react';
import type { ControllerRenderProps } from 'react-hook-form';
import { Input, Label, Button, Tooltip } from '@/shared/components';

export const ESTIMATE_TOOLTIP =
    "Estimated from this vehicle's MOT history. " +
    'Select Use estimate while the field is empty to insert it, or enter the actual figure if you have it.';

interface EstimateAcceptFieldProps {
    id: string;
    label: string;
    estimate: number | null | undefined;
    unitSuffix: string;
    emptyPlaceholder: string;
    isLoading?: boolean;
    field: Pick<ControllerRenderProps, 'value' | 'onChange'>;
    error?: string;
    // Required, so a caller cannot pass `error` without the id and silently lose the message node.
    // The shared Input's hint node has no id, so this component renders its own message node.
    errorId: string;
    // True only for react-hook-form's post-interaction fieldState.error, never a resting fieldIssues message,
    // so role="alert" is not announced on mount for a merely seeded value.
    hasFieldError?: boolean;
    tooltip?: string;
    isRequired?: boolean;
    inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
}

export const EstimateAcceptField: React.FC<EstimateAcceptFieldProps> = ({
    id,
    label,
    estimate,
    unitSuffix,
    emptyPlaceholder,
    isLoading = false,
    field,
    error,
    errorId,
    hasFieldError = false,
    tooltip = ESTIMATE_TOOLTIP,
    isRequired = false,
    inputMode,
}) => {
    const { value: fieldValue, onChange: fieldOnChange } = field;
    const hasEstimate = typeof estimate === 'number';
    const isEmpty = fieldValue === undefined || fieldValue === '' || fieldValue === null;

    const placeholder = hasEstimate
        ? `Estimated ${Math.round(estimate as number).toLocaleString('en-GB')} ${unitSuffix} (select Use estimate)`
        : emptyPlaceholder;

    const insertEstimate = useCallback(() => {
        if (hasEstimate) fieldOnChange(Math.round(estimate as number));
    }, [hasEstimate, estimate, fieldOnChange]);

    // Coerces the input's string to a number for z.number(). Empty yields undefined, not null: undefined is a user
    // clear, null is the unaccepted-estimate seed, and the quote merge treats them differently.
    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const raw = e.target.value;
            if (raw === '') {
                fieldOnChange(undefined);
                return;
            }
            const parsed = Number(raw);
            fieldOnChange(Number.isNaN(parsed) ? undefined : parsed);
        },
        [fieldOnChange],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            const currentIsEmpty =
                fieldValue === undefined || fieldValue === '' || fieldValue === null;
            if (e.key === 'ArrowDown' && hasEstimate && currentIsEmpty) {
                e.preventDefault();
                insertEstimate();
            }
        },
        [hasEstimate, fieldValue, insertEstimate],
    );

    const hintId = `${id}-estimate-hint`;

    // Composes the hint and error ids, each only when its node renders, so aria-describedby never dangles.
    const describedBy =
        [hasEstimate ? hintId : undefined, error ? errorId : undefined]
            .filter((value): value is string => Boolean(value))
            .join(' ') || undefined;

    return (
        <div>
            {hasEstimate ? (
                <Label htmlFor={id}>
                    <span className="inline-flex items-center gap-1">
                        {label}
                        <Tooltip content={tooltip}>
                            <Info size={14} aria-hidden className="text-mp-muted" />
                        </Tooltip>
                    </span>
                </Label>
            ) : (
                <Label htmlFor={id}>{label}</Label>
            )}
            {hasEstimate && (
                <p id={hintId} className="sr-only">
                    {tooltip}
                </p>
            )}
            <div className="flex items-start gap-2">
                <div className="flex-1">
                    <Input
                        type="number"
                        id={id}
                        value={fieldValue === null || fieldValue === undefined ? '' : (fieldValue as number | string)}
                        onChange={handleChange}
                        onKeyDown={hasEstimate ? handleKeyDown : undefined}
                        error={!!error}
                        aria-invalid={!!error}
                        placeholder={placeholder}
                        aria-describedby={describedBy}
                        aria-required={isRequired || undefined}
                        inputMode={inputMode}
                        autoComplete="off"
                    >
                        {isLoading && (
                            <span
                                className="absolute -translate-y-1/2 right-3 top-1/2 pointer-events-none text-mp-muted"
                                aria-hidden
                                data-testid={`${id}-loading`}
                            >
                                <Loader2 className="size-4 animate-spin" />
                            </span>
                        )}
                    </Input>
                    {/* Own message node because Input's hint has no id; role="alert" follows hasFieldError only. */}
                    {error && (
                        <p id={errorId} role={hasFieldError ? 'alert' : undefined} className="mt-1.5 text-xs text-mp-error">
                            {error}
                        </p>
                    )}
                </div>
                {hasEstimate && isEmpty && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={insertEstimate}
                        ariaLabel={`Use ${label} estimate`}
                    >
                        Use estimate
                    </Button>
                )}
            </div>
        </div>
    );
};
